import React, { useEffect, useMemo, useRef, useState } from 'react';
import { collection, getDocs, orderBy, query, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { hasScoredParticipations } from '../../utils/showLiveWindow';

const parseShowDay = (value) => {
  if (!value) return null;
  if (value instanceof Date) {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }
  if (typeof value?.toDate === 'function') {
    const date = value.toDate();
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  if (typeof value === 'string') {
    const italianDateMatch = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (italianDateMatch) {
      const [, day, month, year] = italianDateMatch;
      return new Date(Number(year), Number(month) - 1, Number(day));
    }
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? null
    : new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
};

const isPleShow = (showData) => String(showData?.type || '').toLowerCase() === 'ple';
const ROTATION_INTERVAL_MS = 50000;
const PROGRESS_INTERVAL_MS = 100;

const getShowTime = (value) => {
  if (!value) return 0;
  if (value instanceof Date) return value.getTime();
  if (typeof value?.toDate === 'function') return value.toDate().getTime();

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
};

const formatShowDate = (value) => {
  if (!value) return '';
  if (value instanceof Date) return value.toLocaleDateString('it-IT');
  if (typeof value?.toDate === 'function') {
    return value.toDate().toLocaleDateString('it-IT');
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '' : parsed.toLocaleDateString('it-IT');
};

const buildShowFromDoc = (docSnap) => {
  const data = docSnap.data();

  return {
    id: docSnap.id,
    nome: data.nome,
    logo: data.logo || '',
    sfondo: data.sfondo || '',
    data: formatShowDate(data.data),
    type: data.type || 'weekly',
  };
};

const sortShowsForRotation = (docs) =>
  [...docs].sort((a, b) => {
    const aIsPle = isPleShow(a.data());
    const bIsPle = isPleShow(b.data());

    if (aIsPle !== bIsPle) return aIsPle ? -1 : 1;
    return getShowTime(b.data().data) - getShowTime(a.data().data);
  });

const sortParticipantsByPoints = (participants = []) =>
  [...participants].sort(
    (a, b) => b.points - a.points || a.name.localeCompare(b.name, 'it')
  )

const groupParticipantsByPoints = (participants = []) => {
  let lastPoints = null;
  let currentRank = 0;

  return sortParticipantsByPoints(participants).reduce((groups, participant, index) => {
    if (participant.points !== lastPoints) {
      currentRank = index + 1;
      groups.push({
        rank: currentRank,
        points: participant.points,
        members: [participant],
      });
    } else {
      groups[groups.length - 1].members.push(participant);
    }

    lastPoints = participant.points;
    return groups;
  }, []);
};

const getTopRankGroups = (participants = []) =>
  groupParticipantsByPoints(participants).filter(group => group.rank <= 5)

const getRankTone = (rank) => ({
  rankClass:
    rank === 1
      ? 'podium-1'
      : rank === 2
        ? 'podium-2'
        : rank === 3
          ? 'podium-3'
          : 'podium-other',
  nameClass:
    rank === 1
      ? 'wrestler-1'
      : rank === 2
        ? 'wrestler-2'
        : rank === 3
          ? 'wrestler-3'
          : 'wrestler-other',
  badgeClass:
    rank === 1
      ? 'badge-1'
      : rank === 2
        ? 'badge-2'
        : rank === 3
          ? 'badge-3'
          : 'badge-other',
});

function UltimoShowCard({ show, participants, navigate }) {
  const [slides, setSlides] = useState(() => [
    {
      show,
      participants: Array.isArray(participants) ? participants : [],
    },
  ]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [rotationProgress, setRotationProgress] = useState(100);
  const latestParticipantsRef = useRef(Array.isArray(participants) ? participants : []);
  const participantsSignature = Array.isArray(participants)
    ? participants.map((participant) => `${participant.id}:${participant.name}:${participant.points}`).join('|')
    : '';

  useEffect(() => {
    latestParticipantsRef.current = Array.isArray(participants) ? participants : [];
  }, [participants]);

  useEffect(() => {
    if (slides.length <= 1) {
      setRotationProgress(100);
      return undefined;
    }

    const decrement = (PROGRESS_INTERVAL_MS / ROTATION_INTERVAL_MS) * 100;
    const intervalId = window.setInterval(() => {
      setRotationProgress((currentProgress) => {
        if (currentProgress <= 0) {
          setActiveIndex((currentIndex) => (currentIndex + 1) % slides.length);
          return 100;
        }

        return Math.max(0, currentProgress - decrement);
      });
    }, PROGRESS_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [slides.length]);

  useEffect(() => {
    setRotationProgress(100);
  }, [activeIndex, slides.length]);

  useEffect(() => {
    let isMounted = true;

    const fallbackSlide = {
      show,
      participants: latestParticipantsRef.current,
    };
    const fallbackSlides = hasScoredParticipations(fallbackSlide.participants)
      ? [fallbackSlide]
      : [];

    const loadSameDayShows = async () => {
      const showDay = parseShowDay(show?.data);
      if (!show?.id || !showDay) {
        setSlides(fallbackSlides);
        setActiveIndex(0);
        return;
      }

      const nextDay = new Date(showDay);
      nextDay.setDate(nextDay.getDate() + 1);

      try {
        const sameDayShowsSnap = await getDocs(
          query(
            collection(db, 'shows'),
            where('data', '>=', showDay),
            where('data', '<', nextDay),
            orderBy('data', 'asc')
          )
        );

        const sameDayShowDocs = sameDayShowsSnap.docs;
        if (sameDayShowDocs.length <= 1) {
          if (!isMounted) return;
          setSlides(fallbackSlides);
          setActiveIndex(0);
          return;
        }

        const sortedShowDocs = sortShowsForRotation(sameDayShowDocs);
        const [wrestlersSnap, participantSnaps] = await Promise.all([
          getDocs(collection(db, 'wrestlers')),
          Promise.all(
            sortedShowDocs.map((docSnap) =>
              getDocs(collection(db, 'shows', docSnap.id, 'participations'))
            )
          ),
        ]);

        const wrestlerNames = {};
        wrestlersSnap.docs.forEach((wrestlerDoc) => {
          const wrestlerData = wrestlerDoc.data();
          wrestlerNames[wrestlerDoc.id] = wrestlerData.name || wrestlerData.nome;
        });

        const nextSlides = sortedShowDocs
          .map((docSnap, index) => ({
            show: buildShowFromDoc(docSnap),
            participants: participantSnaps[index].docs.map((participantDoc) => {
              const data = participantDoc.data();
              return {
                id: data.wrestlerId,
                name: wrestlerNames[data.wrestlerId] || 'Sconosciuto',
                points: data.totalPoints ?? 0,
              };
            }),
          }))
          .filter((slideItem) => hasScoredParticipations(slideItem.participants));

        if (!isMounted) return;
        setSlides(nextSlides);
        setActiveIndex(0);
      } catch (error) {
        console.error('Errore caricamento ultimo show:', error);
        if (!isMounted) return;
        setSlides(fallbackSlides);
        setActiveIndex(0);
      }
    };

    loadSameDayShows();

    return () => {
      isMounted = false;
    };
  }, [participantsSignature, show]);

  const activeSlide = slides[activeIndex] || slides[0] || {
    show,
    participants: latestParticipantsRef.current,
  };
  const displayShow = activeSlide.show || show;
  const displayParticipants = useMemo(
    () => (Array.isArray(activeSlide.participants) ? activeSlide.participants : []),
    [activeSlide.participants]
  );
  const groupedTopParticipants = useMemo(
    () => getTopRankGroups(displayParticipants),
    [displayParticipants]
  );

  if (!hasScoredParticipations(activeSlide.participants)) {
    return null;
  }
  const showCardStyle = displayShow?.sfondo
    ? {
        minHeight: '400px',
        backgroundColor: 'transparent',
        backgroundImage: `url(${displayShow.sfondo})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }
    : {
        minHeight: '400px',
        backgroundColor: 'var(--brand-primary)',
        backgroundImage: 'linear-gradient(135deg, var(--brand-primary) 0%, #1d2c39 100%)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      };

  return (
    <div className="mb-4">
      <div className="card-link">
        <div
          className="card show-themed brand-card overflow-hidden position-relative"
          role="button"
          tabIndex={0}
          style={showCardStyle}
        >
          {slides.length > 1 && (
            <div
              className="position-absolute bottom-0 start-0 end-0"
              style={{ height: '3px', zIndex: 5 }}
            >
              <div
                className="h-100 bg-brand-secondary"
                style={{
                  width: `${rotationProgress}%`,
                  transition: `width ${PROGRESS_INTERVAL_MS}ms linear`,
                }}
              />
            </div>
          )}

          <div className="position-absolute top-0 start-0 w-100 h-100 z-0 overlay-brand"></div>

          <div className="position-relative z-1 h-100 d-flex flex-column">
            <div className="card-header show-header brand-header">
              <div className="d-flex align-items-center justify-content-between">
                <h3 className="card-title mb-0">
                  <i className="bi bi-calendar3 me-2 brand-title"></i>
                  Ultimo Show
                </h3>
                <span className="badge show-data px-2 py-1">{displayShow.data}</span>
              </div>
            </div>

            <div className="card-body d-flex flex-column flex-grow-1">
              <div
                onClick={() => navigate(`/shows/${displayShow.id}`)}
                onKeyDown={(e) =>
                  e.key === 'Enter' && navigate(`/shows/${displayShow.id}`)
                }
                className="cursor-pointer flex-grow-1"
              >
                <div className="text-center mb-4 mt-2">
                  {displayShow.logo && (
                    <img
                      src={displayShow.logo}
                      alt={displayShow.nome}
                      className="img-fluid show-logo"
                      style={{
                        maxHeight: '70px',
                        maxWidth: '200px',
                      }}
                    />
                  )}
                </div>

                <div className="d-flex justify-content-between align-items-center show-divider mb-3 pb-2">
                  <h5 className="section-title brand-title">
                    TOP 5
                  </h5>
                  <span className="show-link brand-link cursor-pointer fs-small">
                    Risultati completi
                    <i className="bi bi-chevron-right ms-1"></i>
                  </span>
                </div>

                {groupedTopParticipants.length > 0 ? (
                  <div className="ultimo-show__results flex-grow-1">
                    {groupedTopParticipants.map((group) => {
                      const { rankClass, nameClass, badgeClass } = getRankTone(group.rank);
                      const namesLabel = group.members
                        .map((member) => member.name)
                        .join(', ');
                      const shouldScroll = namesLabel.length > 24;

                      return (
                        <div key={`${group.rank}-${group.points}`} className="ultimo-show__row">
                          <div className="d-flex align-items-center gap-3 flex-grow-1 min-w-0">
                            <span className={`ultimo-show__rank ${rankClass}`}>
                              {group.rank === 1 && <i className="bi bi-trophy-fill"></i>}
                              {group.rank === 2 && <i className="bi bi-award-fill"></i>}
                              {group.rank === 3 && <i className="bi bi-award"></i>}
                              {group.rank > 3 && <span>{group.rank}</span>}
                            </span>

                            <div className="ultimo-show__copy min-w-0">
                              <div
                                className={`ultimo-show__names ${nameClass} ${shouldScroll ? 'is-marquee' : ''}`}
                              >
                                {shouldScroll ? (
                                  <div className="ultimo-show__marquee-track">
                                    <span>{namesLabel}</span>
                                    <span aria-hidden="true">{namesLabel}</span>
                                  </div>
                                ) : (
                                  <span>{namesLabel}</span>
                                )}
                              </div>
                            </div>
                          </div>

                          <span
                            className={`badge show-badge rounded-pill px-3 ${badgeClass}`}
                          >
                            {group.points}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-4 flex-grow-1 d-flex flex-column justify-content-center">
                    <i className="bi bi-people text-muted-brand fs-1 mb-3"></i>
                    <p className="text-muted-brand mb-0">Nessun partecipante registrato</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UltimoShowCard;
